import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  FormControlLabel,
  Switch,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageIcon from '@mui/icons-material/Image';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import OptionalImageField from '../common/OptionalImageField';
import SubQuestionDialog from './SubQuestionDialog';
import MathEditor from './MathEditor';
import MathRenderer from '../common/MathRenderer';
import AIGeneratedQuestionCard from './AIGeneratedQuestionCard';
import TagSelector from './TagSelector';
import { TopicAutocomplete } from '../common/TopicAutocomplete';
import { metaTagService } from '../../services/metaTagService';

const QUESTION_TYPES = [
  { value: 'MULTIPLE', label: 'Multiple Choice' },
  { value: 'TRUE_FALSE', label: 'True/False' },
  { value: 'SHORT_ANSWER', label: 'Short Answer' },
];

const emptyRow = (id, examModeDefault = '') => ({
  id,
  persistedQuestionId: null,
  board: '',
  academic_year: '',
  difficulty: '',
  // Full list of board/year/difficulty pairs for this question
  board_years: [{ board: '', academic_year: '', difficulty: '' }],
  subject: '',
  topic: '',
  question_text: '',
  question_type: 'SHORT_ANSWER',
  options: '',
  correct_answer: '',
  exam_mode: examModeDefault === '' || examModeDefault == null ? '' : examModeDefault,
  has_subquestions: false,
  sub_questions_data: [],
  imageFile: null,
  imagePreview: '',
  subQuestionImageFiles: {},
  tags: [],
  status: null,
  is_active: true,
  saveState: 'draft',
});

function parseOptions(options) {
  if (options == null) return [];
  if (Array.isArray(options)) return options.map((o) => (o == null ? '' : String(o)));
  const text = String(options).trim();
  if (!text) return [];
  try {
    const parsed = JSON.parse(text);
    return Array.isArray(parsed) ? parsed.map((o) => String(o)) : [];
  } catch {
    return text.split(',').map((s) => s.trim()).filter(Boolean);
  }
}

/** Returns { isValid, errors, errorKeys } - errorKeys for field-level highlighting */
function validateRow(row) {
  const errors = [];
  const errorKeys = [];
  if (!(typeof row.question_text === 'string' && row.question_text.trim())) {
    errors.push('Question text is required');
    errorKeys.push('question_text');
  }
  if (!row.question_type) {
    errors.push('Question type is required');
    errorKeys.push('question_type');
  }
  if (!row.subject) {
    errors.push('Subject is required');
    errorKeys.push('subject');
  }
  if (!row.topic) {
    errors.push('Topic is required');
    errorKeys.push('topic');
  }
  // Validate board/year/difficulty via board_years array (falls back to single selectors)
  const pairsSource =
    (Array.isArray(row.board_years) && row.board_years.length > 0
      ? row.board_years
      : [{ board: row.board, academic_year: row.academic_year, difficulty: row.difficulty }]) || [];
  const completePairs = pairsSource.filter(
    (p) => p.board && p.academic_year && p.difficulty
  );
  if (completePairs.length === 0) {
    errors.push('Board, Academic Year, and Difficulty are required');
    errorKeys.push('board_years');
  } else {
    const keys = completePairs.map((p) => `${p.board}|${p.academic_year}|${p.difficulty}`);
    const keyCount = {};
    keys.forEach((k) => { keyCount[k] = (keyCount[k] || 0) + 1; });
    if (Object.values(keyCount).some((c) => c > 1)) {
      errors.push('The same Board, Year and Difficulty combination cannot be added more than once');
      errorKeys.push('board_years');
    }
    const byClass = {};
    completePairs.forEach((p) => {
      const k = `${p.board}|${p.academic_year}`;
      if (!byClass[k]) byClass[k] = new Set();
      byClass[k].add(String(p.difficulty));
    });
    if (Object.values(byClass).some((s) => s.size > 1)) {
      errors.push('For the same class (Board + Year), only one difficulty level is allowed');
      errorKeys.push('board_years');
    }
  }
  const hasSubQuestions = (row.sub_questions_data || []).length > 0;
  if (hasSubQuestions) {
    if (!row.sub_questions_data || !Array.isArray(row.sub_questions_data) || row.sub_questions_data.length === 0) {
      errors.push('At least one sub-question is required');
      errorKeys.push('sub_questions');
    } else {
      row.sub_questions_data.forEach((sq, i) => {
        if (!(typeof sq.question_text === 'string' && sq.question_text.trim())) errorKeys.push('sub_questions');
        if (sq.question_type === 'MULTIPLE' && (!parseOptions(sq.options) || parseOptions(sq.options).length === 0)) errorKeys.push('sub_questions');
      });
    }
  }
  if (!hasSubQuestions) {
    if (row.question_type === 'MULTIPLE') {
      const opts = parseOptions(row.options);
      if (!Array.isArray(opts) || opts.length === 0) {
        errors.push('Options required for Multiple Choice');
        errorKeys.push('options');
      } else {
        const answer = typeof row.correct_answer === 'string' ? row.correct_answer.trim() : row.correct_answer;
        const normalizedOpts = opts.map((o) => String(o).trim());
        if (answer && !normalizedOpts.includes(answer)) {
          errors.push('Correct answer must be one of the options');
          errorKeys.push('correct_answer');
        }
      }
    }
    if (row.question_type === 'TRUE_FALSE') {
      if (row.correct_answer && !['True', 'False'].includes(row.correct_answer)) {
        errors.push('Correct answer must be True or False when provided');
        errorKeys.push('correct_answer');
      }
    }
  }
  return { isValid: errors.length === 0, errors, errorKeys };
}

function withBatchDefaults(row, batchSubject, batchTopic, batchBoard, batchAcademicYear) {
  return {
    ...row,
    board: batchBoard || row.board || '',
    academic_year: batchAcademicYear || row.academic_year || '',
    subject: row.subject || batchSubject || '',
    topic: row.topic || batchTopic || '',
    board_years: [
      {
        board: batchBoard || row.board || '',
        academic_year: batchAcademicYear || row.academic_year || '',
        difficulty: row.difficulty || '',
      },
    ],
  };
}

function rowHasMeaningfulContent(row) {
  const boardYearPairs = Array.isArray(row.board_years) ? row.board_years : [];
  const hasDifficultySelection =
    Boolean(row.difficulty) || boardYearPairs.some((pair) => pair?.difficulty);
  return Boolean(
    row.persistedQuestionId ||
    (typeof row.question_text === 'string' && row.question_text.trim()) ||
    hasDifficultySelection ||
    row.imageFile ||
    row.imagePreview ||
    (Array.isArray(row.sub_questions_data) && row.sub_questions_data.length > 0) ||
    (Array.isArray(row.tags) && row.tags.length > 0) ||
    (typeof row.correct_answer === 'string' && row.correct_answer.trim()) ||
    (typeof row.options === 'string' && row.options.trim()) ||
    row.question_type !== 'SHORT_ANSWER' ||
    row.is_active === false
  );
}

const MultiQuestionEditorDialog = ({
  open,
  onClose,
  onSaveRows,
  onUpdateRows,
  onDeleteRow,
  onOpenSingleQuestion,
  onOpenDetailAnswer,
  subjects,
  allTopics,
  boards,
  academicYears,
  curriculumAssociations = [],
  difficulties = [],
  examModes = [],
  statuses = [],
  isChampions = false,
  role = 'teacher',
}) => {
  const [rows, setRows] = useState(() => [emptyRow(1)]);
  const [batchBoard, setBatchBoard] = useState('');
  const [batchAcademicYear, setBatchAcademicYear] = useState('');
  const [batchSubject, setBatchSubject] = useState('');
  const [batchChapter, setBatchChapter] = useState('');
  const [batchTopic, setBatchTopic] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [validationSummary, setValidationSummary] = useState(null);
  const [subQuestionDialogOpen, setSubQuestionDialogOpen] = useState(false);
  const [editingSubQuestionRowId, setEditingSubQuestionRowId] = useState(null);
  const [imageDialogRowId, setImageDialogRowId] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [answerDialogRowId, setAnswerDialogRowId] = useState(null);
  const subQuestionImageFilesRef = useRef({});
  const subQuestionDetailImageFilesRef = useRef({});
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [dialogSize, setDialogSize] = useState(() => {
    if (typeof window !== 'undefined') {
      return {
        width: Math.round(window.innerWidth * 0.7),
        height: Math.round(window.innerHeight * 0.7),
      };
    }
    return { width: 1200, height: 700 };
  });

  const draftStatusId = useMemo(() => {
    const list = statuses || [];
    const draft = list.find((s) => s.value === 'Draft');
    return draft ? draft.id : null;
  }, [statuses]);

  React.useEffect(() => {
    if (!draftStatusId || !(statuses || []).length) return;
    setRows((prev) => {
      let changed = false;
      const next = prev.map((r) => {
        if (r.status === null || r.status === undefined || r.status === '') {
          changed = true;
          return { ...r, status: draftStatusId };
        }
        return r;
      });
      return changed ? next : prev;
    });
  }, [draftStatusId, statuses]);

  const topicsBySubject = useMemo(() => {
    const map = {};
    (allTopics || []).forEach((t) => {
      const subjId = t.subject ?? t.subject_id ?? (t.subject && t.subject.id);
      const key = String(subjId ?? '');
      if (!map[key]) map[key] = [];
      map[key].push(t);
    });
    return map;
  }, [allTopics]);

  const curriculumScope = useMemo(() => {
    const exact = (curriculumAssociations || []).filter((assoc) => (
      assoc.is_active !== false &&
      String(assoc.board) === String(batchBoard) &&
      String(assoc.academic_year) === String(batchAcademicYear) &&
      String(assoc.subject) === String(batchSubject)
    ));
    const chaptersById = new Map();
    exact.forEach((assoc) => {
      const chapterId = assoc.parent_mapping_topic;
      const chapterName = assoc.parent_mapping_topic_name || '';
      if (chapterId != null && chapterName && !chaptersById.has(String(chapterId))) {
        chaptersById.set(String(chapterId), {
          id: chapterId,
          topic: chapterName,
          name: chapterName,
        });
      }
    });
    const topicsByChapterId = {};
    exact.forEach((assoc) => {
      const chapterId = assoc.parent_mapping_topic;
      if (chapterId == null || String(chapterId) === String(assoc.topic)) return;
      const chapterKey = String(chapterId);
      if (!topicsByChapterId[chapterKey]) topicsByChapterId[chapterKey] = [];
      topicsByChapterId[chapterKey].push({
        id: assoc.topic,
        topic: assoc.topic_name || '',
        name: assoc.topic_name || '',
      });
    });
    Object.keys(topicsByChapterId).forEach((key) => {
      const seen = new Set();
      topicsByChapterId[key] = topicsByChapterId[key]
        .filter((topic) => {
          if (topic.id == null || seen.has(String(topic.id))) return false;
          seen.add(String(topic.id));
          return true;
        })
        .sort((a, b) => (a.topic || '').localeCompare(b.topic || ''));
    });
    return {
      chapters: Array.from(chaptersById.values()).sort((a, b) => (a.topic || '').localeCompare(b.topic || '')),
      topicsByChapterId,
    };
  }, [curriculumAssociations, batchBoard, batchAcademicYear, batchSubject]);

  const batchTopicOptions = useMemo(
    () => curriculumScope.topicsByChapterId[String(batchChapter)] || [],
    [curriculumScope, batchChapter]
  );

  const validExamModeIds = useMemo(
    () => new Set((examModes || []).map((m) => String(m.id))),
    [examModes]
  );

  /** Only Exam and Practice appear in "Used in" (by mode name). */
  const usedInModeOptions = useMemo(() => {
    const list = examModes || [];
    return list.filter((m) => {
      const mode = String(m.mode || '').trim().toLowerCase();
      return mode === 'exam' || mode === 'practice';
    });
  }, [examModes]);

  const allowedUsedInIds = useMemo(
    () => new Set(usedInModeOptions.map((m) => String(m.id))),
    [usedInModeOptions]
  );

  const defaultExamModeId = useMemo(() => {
    const exam = usedInModeOptions.find((m) => String(m.mode || '').trim().toLowerCase() === 'exam');
    if (exam) return exam.id;
    const practice = usedInModeOptions.find((m) => String(m.mode || '').trim().toLowerCase() === 'practice');
    return practice ? practice.id : '';
  }, [usedInModeOptions]);

  /** When dialog opens or modes load, default invalid / empty "Used in" to Exam. */
  React.useEffect(() => {
    if (!open || defaultExamModeId === '' || defaultExamModeId == null) return;
    setRows((prev) => {
      let changed = false;
      const next = prev.map((r) => {
        if (allowedUsedInIds.has(String(r.exam_mode))) return r;
        changed = true;
        return { ...r, exam_mode: defaultExamModeId };
      });
      return changed ? next : prev;
    });
  }, [open, defaultExamModeId, usedInModeOptions, allowedUsedInIds]);

  const validationByRowId = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      const v = validateRow(withBatchDefaults(r, batchSubject, batchTopic, batchBoard, batchAcademicYear));
      map[r.id] = v;
    });
    return map;
  }, [rows, batchSubject, batchTopic, batchBoard, batchAcademicYear]);

  const appendBlankRow = useCallback(() => {
    setRows((prev) => {
      const nextId = prev.length > 0 ? Math.max(...prev.map((r) => r.id)) + 1 : 1;
      const base = emptyRow(nextId, defaultExamModeId);
      return [
        ...prev,
        {
          ...base,
          board: batchBoard,
          academic_year: batchAcademicYear,
          subject: batchSubject,
          topic: batchTopic,
          board_years: [{ board: batchBoard, academic_year: batchAcademicYear, difficulty: '' }],
          status: base.status ?? draftStatusId ?? base.status,
        },
      ];
    });
    setValidationSummary(null);
  }, [draftStatusId, batchSubject, batchTopic, batchBoard, batchAcademicYear, defaultExamModeId]);

  const removeRow = useCallback(async (id) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    if (row.persistedQuestionId && onDeleteRow) {
      setSubmitting(true);
      try {
        const deleted = await onDeleteRow(row.persistedQuestionId);
        if (deleted === false) {
          setSubmitting(false);
          return;
        }
      } catch (err) {
        setValidationSummary({ errors: [err?.message || 'Delete failed'] });
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
    }
    setRows((prev) => {
      const next = prev.filter((r) => r.id !== id);
      return next.length > 0 ? next : [emptyRow(1, defaultExamModeId)];
    });
    setValidationSummary(null);
  }, [rows, onDeleteRow, defaultExamModeId]);

  const updateRow = useCallback((id, field, value) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const next = { ...r, [field]: value };
        // Keep board_years[0] in sync with the primary selectors shown in the table
        if (field === 'board' || field === 'academic_year' || field === 'difficulty') {
          const basePairs =
            Array.isArray(next.board_years) && next.board_years.length > 0
              ? [...next.board_years]
              : [{ board: next.board || '', academic_year: next.academic_year || '', difficulty: next.difficulty || '' }];
          const first = basePairs[0] || { board: '', academic_year: '', difficulty: '' };
          const updatedFirst = {
            ...first,
            board: batchBoard || first.board || '',
            academic_year: batchAcademicYear || first.academic_year || '',
            [field === 'board' ? 'board' : field]: value,
          };
          basePairs[0] = updatedFirst;
          next.board_years = basePairs;
        }
        if (field === 'subject') next.topic = '';
        if (field === 'question_type' && value === 'TRUE_FALSE') next.options = '["True", "False"]';
        if (field === 'has_subquestions' && value) next.correct_answer = '';
        if (r.persistedQuestionId) next.saveState = 'dirty';
        return next;
      })
    );
    setValidationSummary(null);
  }, [batchBoard, batchAcademicYear]);

  const editingRow = useMemo(() => (editingSubQuestionRowId ? rows.find((r) => r.id === editingSubQuestionRowId) : null), [rows, editingSubQuestionRowId]);

  const handleSubQuestionSave = useCallback(
    (updatedSubQuestions, imageFilesByIndex = {}, detailImageFilesByIndex = {}) => {
      if (!editingSubQuestionRowId) return;
      const files = imageFilesByIndex && typeof imageFilesByIndex === 'object' ? imageFilesByIndex : {};
      subQuestionImageFilesRef.current[editingSubQuestionRowId] = { ...(subQuestionImageFilesRef.current[editingSubQuestionRowId] || {}), ...files };
      const detailFiles = detailImageFilesByIndex && typeof detailImageFilesByIndex === 'object' ? detailImageFilesByIndex : {};
      subQuestionDetailImageFilesRef.current[editingSubQuestionRowId] = { ...(subQuestionDetailImageFilesRef.current[editingSubQuestionRowId] || {}), ...detailFiles };
      const hasSub = (updatedSubQuestions || []).length > 0;
      setRows((prev) =>
        prev.map((r) => {
          if (r.id !== editingSubQuestionRowId) return r;
          return {
            ...r,
            has_subquestions: hasSub,
            correct_answer: hasSub ? '' : r.correct_answer,
            sub_questions_data: (updatedSubQuestions || []).map((sq, idx) => ({ ...sq, order: idx + 1 })),
            saveState: r.persistedQuestionId ? 'dirty' : r.saveState,
          };
        })
      );
      // Do not close the dialog here: SubQuestionDialog auto-saves on every change, so closing
      // on each save caused the dialog to close while typing. Dialog closes only when user
      // clicks Done, Save & Close, or X (via onClose).
    },
    [editingSubQuestionRowId]
  );

  /** Process tags for a row: create new tags via API, return array of tag IDs (same behaviour as single question). */
  const processRowTags = useCallback(async (rawTags) => {
    if (!Array.isArray(rawTags) || rawTags.length === 0) return [];
    const newTags = [];
    const existingTagIds = [];
    const tagNames = [];
    for (const tag of rawTags) {
      if (!tag) continue;
      if (typeof tag === 'number') existingTagIds.push(tag);
      else if (typeof tag === 'string') {
        const trimmed = tag.trim();
        if (/^\d+$/.test(trimmed)) existingTagIds.push(Number(trimmed));
        else if (trimmed) tagNames.push(trimmed);
      }
      else if (typeof tag === 'object') {
        if (tag._isNew === true) newTags.push(tag);
        else if (tag.id && typeof tag.id === 'number') existingTagIds.push(tag.id);
        else if (tag.name && typeof tag.name === 'string') tagNames.push(tag.name.trim());
      }
    }
    const resolveExistingTagIdsByName = async (names) => {
      if (!names.length) return [];
      try {
        metaTagService.clearCache();
        const allTags = await metaTagService.getActiveTags();
        const list = Array.isArray(allTags) ? allTags : (allTags?.results || []);
        return names
          .map((name) => list.find((t) => String(t.name).trim().toLowerCase() === String(name).trim().toLowerCase())?.id)
          .filter((id) => typeof id === 'number' && id > 0);
      } catch {
        return [];
      }
    };
    const resolvedNameIds = await resolveExistingTagIdsByName(tagNames);
    const unresolvedNewTags = newTags.filter((tag) => !resolvedNameIds.includes(tag.id));
    if (unresolvedNewTags.length > 0) {
      try {
        const createdTagIds = [];
        for (const newTag of unresolvedNewTags) {
          if (!newTag.name || typeof newTag.name !== 'string') continue;
          try {
            const createdTag = await metaTagService.create({
              name: newTag.name.trim(),
              color: newTag.color || '#1976d2',
              is_active: true,
            });
            if (createdTag && createdTag.id) createdTagIds.push(createdTag.id);
          } catch (error) {
            const existingIds = await resolveExistingTagIdsByName([newTag.name]);
            createdTagIds.push(...existingIds);
          }
        }
        if (createdTagIds.length > 0) metaTagService.clearCache();
        return [...new Set([...existingTagIds, ...resolvedNameIds, ...createdTagIds])]
          .filter((id) => typeof id === 'number' && id > 0);
      } catch {
        return [...new Set([...existingTagIds, ...resolvedNameIds])]
          .filter((id) => typeof id === 'number' && id > 0);
      }
    }
    return [...new Set([...existingTagIds, ...resolvedNameIds])]
      .filter((id) => typeof id === 'number' && id > 0);
  }, []);

  const resetDialog = useCallback(() => {
    setBatchBoard('');
    setBatchAcademicYear('');
    setBatchSubject('');
    setBatchChapter('');
    setBatchTopic('');
    setRows(() => {
      const base = emptyRow(1, defaultExamModeId);
      return [
        {
          ...base,
          status: base.status ?? draftStatusId ?? base.status,
        },
      ];
    });
    setValidationSummary(null);
    subQuestionImageFilesRef.current = {};
    subQuestionDetailImageFilesRef.current = {};
    onClose();
  }, [onClose, draftStatusId, defaultExamModeId]);

  const flashValidationErrors = useCallback((errors) => {
    setValidationSummary(null);
    const nextErrors = Array.isArray(errors) && errors.length > 0
      ? errors
      : ['Please fix the highlighted issues and try again.'];
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        setValidationSummary({ errors: nextErrors });
      });
    } else {
      setValidationSummary({ errors: nextErrors });
    }
  }, []);

  const persistRows = useCallback(async ({ appendAfterSave = false, closeAfterSave = false } = {}) => {
    const draftRows = rows.filter((r) => !r.persistedQuestionId);
    const dirtyRows = rows.filter((r) => r.persistedQuestionId && r.saveState === 'dirty');
    const rowsToPersist = [...draftRows, ...dirtyRows];
    if (rowsToPersist.length === 0) {
      if (appendAfterSave) appendBlankRow();
      if (closeAfterSave) resetDialog();
      return;
    }
    const invalidRows = rowsToPersist
      .map((r) => ({ row: r, validation: validationByRowId[r.id] }))
      .filter(({ validation }) => !validation?.isValid);
    if (invalidRows.length > 0) {
      const errors = invalidRows.flatMap(({ row, validation }) => {
        const rowIndex = rows.findIndex((r) => r.id === row.id);
        return (validation?.errors || []).map((e) => `Row ${rowIndex + 1}: ${e}`);
      });
      flashValidationErrors(errors);
      return;
    }
    setValidationSummary(null);
    setSubmitting(true);
    try {
      const buildPayloads = async (targetRows) => {
        const processedTagsByRow = await Promise.all(targetRows.map((row) => processRowTags(row.tags || [])));
        return targetRows.map((row, i) => {
          const effectiveRow = withBatchDefaults(row, batchSubject, batchTopic, batchBoard, batchAcademicYear);
          const boardYearPairs =
            (Array.isArray(effectiveRow.board_years) && effectiveRow.board_years.length > 0
              ? effectiveRow.board_years
              : [{ board: effectiveRow.board, academic_year: effectiveRow.academic_year, difficulty: effectiveRow.difficulty }]) || [];
          const board_years = boardYearPairs
            .filter((p) => p.board && p.academic_year && p.difficulty)
            .map((p) => ({
              board: parseInt(p.board, 10),
              academic_year: parseInt(p.academic_year, 10),
              difficulty: parseInt(p.difficulty, 10),
            }));
          const has_subquestions = Boolean((effectiveRow.sub_questions_data || []).length > 0);
          let options = [];
          let correct_answer = '';
          let sub_questions_data = [];
          if (has_subquestions) {
            sub_questions_data = (effectiveRow.sub_questions_data || []).map((sq, index) => ({
              question_text: sq.question_text || '',
              question_type: sq.question_type || 'MULTIPLE',
              options: sq.question_type === 'MULTIPLE' ? parseOptions(sq.options) : [],
              correct_answer: sq.correct_answer || '',
              detail_answer: sq.detail_answer || '',
              marks: sq.marks ?? 1,
              order: index + 1,
              is_active: sq.is_active !== undefined ? sq.is_active : true,
            }));
          } else {
            options = effectiveRow.question_type === 'TRUE_FALSE' ? ['True', 'False'] : parseOptions(effectiveRow.options);
            correct_answer = effectiveRow.correct_answer.trim();
          }
          return {
            question_text: effectiveRow.question_text.trim(),
            subject: effectiveRow.subject,
            topic: effectiveRow.topic,
            question_type: effectiveRow.question_type,
            board_years,
            has_subquestions,
            sub_questions_data,
            tags: processedTagsByRow[i] || [],
            is_active: effectiveRow.is_active !== false,
            exam_mode: (() => {
              const raw = effectiveRow.exam_mode;
              const resolved =
                raw != null && raw !== '' && allowedUsedInIds.has(String(raw))
                  ? raw
                  : defaultExamModeId;
              return resolved != null && resolved !== '' && validExamModeIds.has(String(resolved))
                ? resolved
                : null;
            })(),
            status: effectiveRow.status === '' ? null : effectiveRow.status,
            options,
            correct_answer,
          };
        });
      };
      const buildImageFileMap = (targetRows) => {
        const imageFilesByRowId = {};
        targetRows.forEach((r) => {
          if (r.imageFile) imageFilesByRowId[r.id] = r.imageFile;
        });
        return imageFilesByRowId;
      };
      const buildSubQuestionFileMap = (targetRows) => {
        const subQuestionImageFilesByRowId = {};
        targetRows.forEach((r) => {
          if (subQuestionImageFilesRef.current[r.id]) subQuestionImageFilesByRowId[r.id] = subQuestionImageFilesRef.current[r.id];
        });
        return subQuestionImageFilesByRowId;
      };
      const buildSubQuestionDetailFileMap = (targetRows) => {
        const subQuestionDetailImageFilesByRowId = {};
        targetRows.forEach((r) => {
          if (subQuestionDetailImageFilesRef.current[r.id]) subQuestionDetailImageFilesByRowId[r.id] = subQuestionDetailImageFilesRef.current[r.id];
        });
        return subQuestionDetailImageFilesByRowId;
      };

      const createdRowIds = {};
      let failedCreateRowIds = [];
      let failedCreateRowErrors = {};
      let updatedRowIds = [];
      let failedUpdateRowIds = [];
      let failedUpdateRowErrors = {};

      if (draftRows.length > 0) {
        const draftRowIds = draftRows.map((r) => r.id);
        const result = await onSaveRows(
          await buildPayloads(draftRows),
          draftRowIds,
          buildImageFileMap(draftRows),
          buildSubQuestionFileMap(draftRows),
          buildSubQuestionDetailFileMap(draftRows)
        );
        Object.assign(createdRowIds, result?.createdRowIds || {});
        failedCreateRowIds = result?.failedRowIds || [];
        failedCreateRowErrors = result?.failedRowErrors || {};
      }

      if (dirtyRows.length > 0 && onUpdateRows) {
        const dirtyRowIds = dirtyRows.map((r) => r.id);
        const payloads = await buildPayloads(dirtyRows);
        const result = await onUpdateRows(
          payloads.map((payload, index) => ({
            rowId: dirtyRowIds[index],
            questionId: dirtyRows[index].persistedQuestionId,
            payload,
          })),
          buildImageFileMap(dirtyRows),
          buildSubQuestionFileMap(dirtyRows),
          buildSubQuestionDetailFileMap(dirtyRows)
        );
        updatedRowIds = result?.updatedRowIds || [];
        failedUpdateRowIds = result?.failedRowIds || [];
        failedUpdateRowErrors = result?.failedRowErrors || {};
      }

      setRows((prev) => prev.map((r) => {
        if (!rowsToPersist.some((target) => target.id === r.id)) return r;
        if (!r.persistedQuestionId) {
          const persistedId =
            createdRowIds[r.id] ??
            createdRowIds[String(r.id)] ??
            r.persistedQuestionId ??
            null;
          return {
            ...r,
            persistedQuestionId: persistedId,
            saveState: persistedId ? 'saved' : r.saveState,
          };
        }
        const updateSucceeded = updatedRowIds.includes(r.id) || updatedRowIds.includes(String(r.id));
        return {
          ...r,
          saveState: updateSucceeded ? 'saved' : r.saveState,
        };
      }));

      if (failedCreateRowIds.length > 0 || failedUpdateRowIds.length > 0) {
        const failedRowIdSet = new Set([
          ...failedCreateRowIds.map((id) => String(id)),
          ...failedUpdateRowIds.map((id) => String(id)),
        ]);
        const combinedErrors = {
          ...failedCreateRowErrors,
          ...failedUpdateRowErrors,
        };
        const detailedErrors = rows
          .filter((row) => failedRowIdSet.has(String(row.id)))
          .map((row) => {
            const rowIndex = rows.findIndex((r) => r.id === row.id);
            const message = combinedErrors[row.id] ?? combinedErrors[String(row.id)] ?? 'Save failed.';
            return `Row ${rowIndex + 1}: ${message}`;
          });
        const errors = detailedErrors.length > 0 ? detailedErrors : ['Some rows could not be saved.'];
        flashValidationErrors(errors);
        return;
      }

      if (appendAfterSave) appendBlankRow();
      if (closeAfterSave) resetDialog();
    } catch (err) {
      const data = err?.response?.data;
      let msg = data?.detail;
      if (msg == null && data && typeof data === 'object') {
        const firstKey = Object.keys(data)[0];
        const val = firstKey ? data[firstKey] : null;
        msg = Array.isArray(val) ? val[0] : val;
      }
      const errors = [msg || err?.message || 'Submit failed'];
      flashValidationErrors(errors);
    } finally {
      setSubmitting(false);
    }
  }, [appendBlankRow, allowedUsedInIds, batchSubject, batchTopic, batchBoard, batchAcademicYear, defaultExamModeId, flashValidationErrors, onSaveRows, onUpdateRows, processRowTags, resetDialog, rows, validationByRowId, validExamModeIds]);

  const handleSmartClose = useCallback(async () => {
    const hasMeaningfulRows = rows.some((row) => rowHasMeaningfulContent(row));
    const hasOnlySharedSelections = !hasMeaningfulRows && Boolean(batchBoard || batchAcademicYear || batchSubject || batchChapter || batchTopic);
    if (!hasMeaningfulRows || hasOnlySharedSelections) {
      resetDialog();
      return;
    }
    const draftRows = rows.filter((r) => !r.persistedQuestionId);
    const dirtyRows = rows.filter((r) => r.persistedQuestionId && r.saveState === 'dirty');
    const rowsNeedingSave = [...draftRows, ...dirtyRows];
    if (rowsNeedingSave.length === 0) {
      resetDialog();
      return;
    }
    const invalidRows = rowsNeedingSave
      .map((r) => ({ row: r, validation: validationByRowId[r.id] }))
      .filter(({ validation }) => !validation?.isValid);
    if (invalidRows.length > 0) {
      const errors = invalidRows.flatMap(({ row, validation }) => {
        const rowIndex = rows.findIndex((r) => r.id === row.id);
        return (validation?.errors || []).map((e) => `Row ${rowIndex + 1}: ${e}`);
      });
      flashValidationErrors(errors);
      return;
    }
    await persistRows({ closeAfterSave: true });
  }, [batchBoard, batchAcademicYear, batchSubject, batchChapter, batchTopic, flashValidationErrors, persistRows, resetDialog, rows, validationByRowId]);

  const handleDialogClose = useCallback((event, reason) => {
    if (reason === 'backdropClick') return;
    handleSmartClose();
  }, [handleSmartClose]);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = dialogSize.width;
    const startH = dialogSize.height;
    const minW = 600;
    const minH = 400;
    const maxW = typeof window !== 'undefined' ? window.innerWidth * 0.95 : 1600;
    const maxH = typeof window !== 'undefined' ? window.innerHeight * 0.95 : 900;

    const moveHandler = (e2) => {
      const dx = e2.clientX - startX;
      const dy = e2.clientY - startY;
      setDialogSize({
        width: Math.min(maxW, Math.max(minW, startW + dx)),
        height: Math.min(maxH, Math.max(minH, startH + dy)),
      });
    };
    const upHandler = () => {
      window.removeEventListener('mousemove', moveHandler);
      window.removeEventListener('mouseup', upHandler);
    };
    window.addEventListener('mousemove', moveHandler);
    window.addEventListener('mouseup', upHandler);
  }, [dialogSize.width, dialogSize.height]);

  if (!open) return null;

  const setRowImage = (rowId, file, preview) => {
    setRows((prev) => prev.map((r) => (
      r.id !== rowId
        ? r
        : {
            ...r,
            imageFile: file || null,
            imagePreview: preview || '',
            saveState: r.persistedQuestionId ? 'dirty' : r.saveState,
          }
    )));
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth={false}
      fullWidth={false}
      fullScreen={isFullScreen}
      PaperProps={{
        sx: {
          ...(isFullScreen
            ? {}
            : {
                width: dialogSize.width,
                height: dialogSize.height,
                maxWidth: '95vw',
                maxHeight: '95vh',
              }),
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Add New Questions</Typography>
        <IconButton
          onClick={() => setIsFullScreen((prev) => !prev)}
          title={isFullScreen ? 'Exit full screen' : 'Full screen'}
          aria-label={isFullScreen ? 'Exit full screen' : 'Full screen'}
        >
          {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Efficiently populate your database by adding multiple questions in this batch editor. Fields marked with * are required.
        </Typography>
        {validationSummary && validationSummary.errors && validationSummary.errors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {validationSummary.errors.slice(0, 15).map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
              {validationSummary.errors.length > 15 && <li>… and {validationSummary.errors.length - 15} more</li>}
            </Box>
          </Alert>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr 1.3fr' }, gap: 2, alignItems: 'flex-start', mb: 2 }}>
          <TextField
            select
            size="small"
            label="Board *"
            value={batchBoard}
            onChange={(e) => {
              const v = e.target.value;
              setBatchBoard(v);
              setBatchChapter('');
              setBatchTopic('');
              setRows((prev) => prev.map((r) => ({
                ...r,
                board: v,
                topic: '',
                board_years: [{ board: v, academic_year: batchAcademicYear, difficulty: r.difficulty || '' }],
                saveState: r.persistedQuestionId ? 'dirty' : r.saveState,
              })));
            }}
            fullWidth
            error={rows.length > 0 && !batchBoard}
          >
            <MenuItem value="">—</MenuItem>
            {(boards || []).map((board) => (
              <MenuItem key={board.id} value={board.id}>{board.name || board.code}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Academic Year *"
            value={batchAcademicYear}
            onChange={(e) => {
              const v = e.target.value;
              setBatchAcademicYear(v);
              setBatchChapter('');
              setBatchTopic('');
              setRows((prev) => prev.map((r) => ({
                ...r,
                academic_year: v,
                topic: '',
                board_years: [{ board: batchBoard, academic_year: v, difficulty: r.difficulty || '' }],
                saveState: r.persistedQuestionId ? 'dirty' : r.saveState,
              })));
            }}
            fullWidth
            error={rows.length > 0 && !batchAcademicYear}
          >
            <MenuItem value="">—</MenuItem>
            {(academicYears || []).map((year) => (
              <MenuItem key={year.id} value={year.id}>{year.year || year.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Subject *"
            value={batchSubject}
            onChange={(e) => {
              const v = e.target.value;
              setBatchSubject(v);
              setBatchChapter('');
              setBatchTopic('');
              setRows((prev) => prev.map((r) => ({
                ...r,
                subject: v,
                topic: '',
                saveState: r.persistedQuestionId ? 'dirty' : r.saveState,
              })));
            }}
            fullWidth
            error={rows.length > 0 && !batchSubject}
          >
            <MenuItem value="">—</MenuItem>
            {(subjects || []).map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Chapter *"
            value={batchChapter}
            onChange={(e) => {
              const v = e.target.value;
              setBatchChapter(v);
              setBatchTopic('');
              setRows((prev) => prev.map((r) => ({
                ...r,
                topic: '',
                saveState: r.persistedQuestionId ? 'dirty' : r.saveState,
              })));
            }}
            fullWidth
            disabled={!batchBoard || !batchAcademicYear || !batchSubject}
            error={rows.length > 0 && batchBoard && batchAcademicYear && batchSubject && !batchChapter}
          >
            <MenuItem value="">—</MenuItem>
            {curriculumScope.chapters.map((chapter) => (
              <MenuItem key={chapter.id} value={chapter.id}>{chapter.topic}</MenuItem>
            ))}
          </TextField>
          <TopicAutocomplete
            options={batchTopicOptions}
            value={batchTopic}
            onChange={(id) => {
              setBatchTopic(id);
              setRows((prev) => prev.map((r) => ({
                ...r,
                topic: id,
                saveState: r.persistedQuestionId ? 'dirty' : r.saveState,
              })));
            }}
            disabled={!batchChapter}
            error={rows.length > 0 && batchChapter && !batchTopic}
            placeholder={batchChapter ? 'Type to search curriculum topics...' : 'Select chapter first'}
            label="Topic *"
            size="small"
            fullWidth
          />
        </Box>
        <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, minHeight: 200, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }}>#</TableCell>
                <TableCell sx={{ width: 56 }}>Actions</TableCell>
                <TableCell sx={{ width: 150 }}>Difficulty *</TableCell>
                <TableCell sx={{ minWidth: 240, width: 260 }}>Question text *</TableCell>
                <TableCell sx={{ width: 56 }}>Image</TableCell>
                <TableCell sx={{ width: 56 }}>Sub-Q</TableCell>
                <TableCell sx={{ width: 130 }}>Type *</TableCell>
                <TableCell sx={{ width: 100 }}>Used in</TableCell>
                <TableCell sx={{ width: 56 }}>Answer</TableCell>
                <TableCell sx={{ width: 56 }}>Detail</TableCell>
                <TableCell sx={{ width: 140 }}>Publish to others</TableCell>
                <TableCell sx={{ minWidth: 160, width: 180 }}>Tags</TableCell>
                <TableCell sx={{ width: 70 }}>Active</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => {
                const v = validationByRowId[row.id] || {};
                const err = (key) => v.errorKeys && v.errorKeys.includes(key);
                const isSavedRow = row.persistedQuestionId !== null && row.persistedQuestionId !== undefined && row.persistedQuestionId !== '';
                return (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <span>{idx + 1}</span>
                        {isSavedRow && (
                          <Typography variant="caption" color={row.saveState === 'dirty' ? 'warning.main' : 'success.main'}>
                            {row.saveState === 'dirty' ? 'Edited' : 'Saved'}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ width: 56 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton size="small" onClick={() => removeRow(row.id)} aria-label="Remove row" disabled={submitting}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ width: 150 }}>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={row.difficulty || ''}
                        onChange={(e) => updateRow(row.id, 'difficulty', e.target.value)}
                        error={err('board_years')}
                      >
                        <MenuItem value="">—</MenuItem>
                        {(difficulties || []).map((difficulty) => (
                          <MenuItem key={difficulty.id} value={difficulty.id}>{difficulty.name}</MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell sx={{ minWidth: 240, width: 260 }}>
                      <MathEditor
                        value={row.question_text ?? ''}
                        onChange={(val) => updateRow(row.id, 'question_text', val)}
                        label=""
                        placeholder="Question text"
                        helperText=""
                        minRows={2}
                        fullWidth
                        showPreview={false}
                        error={err('question_text')}
                      />
                      {row.question_text && (
                        <Box sx={{ mt: 0.5, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>Preview</Typography>
                          <MathRenderer content={row.question_text} variant="body2" />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ width: 56 }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => setImageDialogRowId(row.id)}
                        title="Image (optional)"
                        aria-label="Attach image"
                      >
                        <ImageIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ width: 56 }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                          setEditingSubQuestionRowId(row.id);
                          setSubQuestionDialogOpen(true);
                        }}
                        title="Sub-questions"
                        aria-label="Sub-questions"
                      >
                        <AccountTreeIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ width: 130 }}>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={row.question_type}
                        onChange={(e) => updateRow(row.id, 'question_type', e.target.value)}
                        error={err('question_type')}
                      >
                        {QUESTION_TYPES.map((t) => (
                          <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell sx={{ width: 100 }}>
                      {usedInModeOptions.length > 0 ? (
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={
                            allowedUsedInIds.has(String(row.exam_mode))
                              ? row.exam_mode
                              : defaultExamModeId
                          }
                          onChange={(e) => {
                            const n = Number(e.target.value);
                            updateRow(
                              row.id,
                              'exam_mode',
                              e.target.value === '' || !Number.isFinite(n) ? defaultExamModeId : n
                            );
                          }}
                        >
                          {usedInModeOptions.map((m) => (
                            <MenuItem key={m.id} value={m.id}>{m.mode}</MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ width: 56 }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => setAnswerDialogRowId(row.id)}
                        title="Answer & options"
                        aria-label="Answer and options"
                        sx={{
                          ...((err('options') || err('correct_answer')) && {
                            border: '1px solid',
                            borderColor: 'error.main',
                            bgcolor: 'error.light',
                            '&:hover': { bgcolor: 'error.main', color: 'white' },
                          }),
                        }}
                      >
                        <CheckCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ width: 56 }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => onOpenDetailAnswer && onOpenDetailAnswer('multi-row-' + row.id)}
                        title="Detail answer"
                        aria-label="Detail answer"
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ width: 140 }}>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={row.status ?? ''}
                        onChange={(e) => updateRow(row.id, 'status', e.target.value)}
                      >
                        {((isChampions || role === 'teacher') ? (statuses || []).filter((s) => s.value === 'Draft' || s.value === 'Under Review') : (statuses || [])).map((statusItem) => (
                          <MenuItem key={statusItem.id} value={statusItem.id}>
                            {statusItem.value === 'Under Review' ? 'Submit for Review' : (statusItem.value || statusItem.mode)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell sx={{ minWidth: 160, width: 180, maxWidth: 180, overflow: 'hidden' }}>
                      <TagSelector
                        value={row.tags || []}
                        onChange={(tags) => updateRow(row.id, 'tags', tags)}
                        label=""
                        helperText=""
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ width: 70 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={row.is_active !== false}
                            onChange={(e) => updateRow(row.id, 'is_active', e.target.checked)}
                          />
                        }
                        label="Active"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Button startIcon={<AddIcon />} onClick={() => persistRows({ appendAfterSave: true })} sx={{ mt: 2 }} disabled={submitting}>
          {submitting ? 'Saving…' : 'Add Question'}
        </Button>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          startIcon={<VisibilityIcon />}
          onClick={() => setPreviewDialogOpen(true)}
          disabled={rows.length === 0}
        >
          Preview
        </Button>
        <Button onClick={handleSmartClose} disabled={submitting} sx={{ display: 'none' }}>
          {submitting ? 'Saving…' : 'Save & Close'}
        </Button>
        <Button onClick={handleSmartClose} disabled={submitting}>
          Close
        </Button>
        <Button onClick={() => persistRows()} disabled={submitting}>
          {submitting ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>

      {/* Resize handle - drag from bottom-right corner (hidden in full screen) */}
      {!isFullScreen && (
      <Box
        onMouseDown={handleResizeStart}
        sx={{
          position: 'absolute',
          right: 0,
          bottom: 0,
          width: 24,
          height: 24,
          cursor: 'se-resize',
          background: 'linear-gradient(135deg, transparent 50%, rgba(0,0,0,0.12) 50%)',
          '&:hover': { background: 'linear-gradient(135deg, transparent 50%, rgba(0,0,0,0.2) 50%)' },
        }}
        aria-label="Resize dialog"
      />
      )}

      {/* Image upload dialog (per row) */}
      <Dialog open={Boolean(imageDialogRowId)} onClose={() => setImageDialogRowId(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Image (optional)</DialogTitle>
        <DialogContent>
          {imageDialogRowId && (() => {
            const row = rows.find((r) => r.id === imageDialogRowId);
            if (!row) return null;
            return (
              <OptionalImageField
                label=""
                previewUrl={row.imagePreview || ''}
                onClear={() => { setRowImage(imageDialogRowId, null, ''); setImageDialogRowId(null); }}
                onFileSelect={(file) => setRowImage(imageDialogRowId, file, file ? URL.createObjectURL(file) : '')}
                onPaste={(file) => setRowImage(imageDialogRowId, file, file ? URL.createObjectURL(file) : '')}
                minHeight={160}
                emptyInstruction="Click or paste screenshot (Ctrl+V) here to attach image"
                attachButtonLabel="Attach Image File"
                inputId={`multi-img-dialog-${imageDialogRowId}`}
                imageAlt="Question"
              />
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialogRowId(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Answer & options dialog (correct answer + options when Multiple Choice) */}
      <Dialog open={Boolean(answerDialogRowId)} onClose={() => setAnswerDialogRowId(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Answer &amp; options</DialogTitle>
        <DialogContent>
          {answerDialogRowId && (() => {
            const row = rows.find((r) => r.id === answerDialogRowId);
            if (!row) return null;
            const v = validationByRowId[row.id] || {};
            const errDialog = (key) => v.errorKeys && v.errorKeys.includes(key);
            const isMultiple = row.question_type === 'MULTIPLE';
            const isTrueFalse = row.question_type === 'TRUE_FALSE';
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                {isMultiple && (
                  <>
                    <TextField
                      label="Options"
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      value={row.options}
                      onChange={(e) => updateRow(answerDialogRowId, 'options', e.target.value)}
                      placeholder="A, B, C, D (one per line or comma-separated)"
                      error={errDialog('options')}
                      helperText="List options for multiple choice. Expand as needed."
                      sx={{ minWidth: 320 }}
                    />
                  </>
                )}
                {isTrueFalse ? (
                  <TextField
                    select
                    label="Correct answer"
                    size="small"
                    fullWidth
                    value={row.correct_answer}
                    onChange={(e) => updateRow(answerDialogRowId, 'correct_answer', e.target.value)}
                    error={errDialog('correct_answer')}
                  >
                    <MenuItem value="">—</MenuItem>
                    <MenuItem value="True">True</MenuItem>
                    <MenuItem value="False">False</MenuItem>
                  </TextField>
                ) : isMultiple ? (
                  (() => {
                    const opts = parseOptions(row.options).map((o) => String(o).trim()).filter(Boolean);
                    const current = typeof row.correct_answer === 'string' ? row.correct_answer.trim() : (row.correct_answer ?? '');
                    const isNotAnOption = Boolean(current) && !opts.includes(current);
                    return (
                      <TextField
                        select
                        label="Correct answer"
                        size="small"
                        fullWidth
                        value={current}
                        onChange={(e) => updateRow(answerDialogRowId, 'correct_answer', e.target.value)}
                        error={errDialog('correct_answer')}
                        disabled={opts.length === 0}
                        helperText={
                          opts.length === 0
                            ? 'Add options above first, then select the correct answer'
                            : isNotAnOption
                              ? `"${current}" is not one of the options — select a valid option`
                              : 'Select the option that is the correct answer'
                        }
                      >
                        <MenuItem value="">—</MenuItem>
                        {isNotAnOption && (
                          <MenuItem value={current}>{current} (not in options)</MenuItem>
                        )}
                        {opts.map((opt, i) => (
                          <MenuItem key={`${opt}-${i}`} value={opt}>{opt}</MenuItem>
                        ))}
                      </TextField>
                    );
                  })()
                ) : (
                  <TextField
                    label="Correct answer"
                    size="small"
                    fullWidth
                    multiline
                    minRows={1}
                    value={row.correct_answer}
                    onChange={(e) => updateRow(answerDialogRowId, 'correct_answer', e.target.value)}
                    placeholder="Correct answer"
                    error={errDialog('correct_answer')}
                  />
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnswerDialogRowId(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Preview all questions (same style as AI generation preview) */}
      <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Preview questions</Typography>
          <IconButton aria-label="close" onClick={() => setPreviewDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {rows.length} question{rows.length !== 1 ? 's' : ''} — same layout as AI generation review.
          </Typography>
          {rows.map((row, index) => {
            const subjectName = (subjects || []).find((s) => String(s.id) === String(row.subject))?.name ?? '';
            const topicObj = (topicsBySubject[String(row.subject)] || []).find((t) => String(t.id) === String(row.topic));
            const topicName = topicObj?.topic ?? topicObj?.name ?? '';
            const questionForCard = {
              question_text: row.question_text ?? '',
              question_type: row.question_type || 'MULTIPLE',
              options: parseOptions(row.options),
              correct_answer: row.correct_answer ?? '',
              topic_name: topicName,
              subject_name: subjectName,
            };
            return (
              <AIGeneratedQuestionCard
                key={row.id}
                question={questionForCard}
                index={index}
                isSelected={false}
                editable={false}
                showDetailAnswer={false}
              />
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <SubQuestionDialog
        open={subQuestionDialogOpen}
        onClose={() => {
          setSubQuestionDialogOpen(false);
          setEditingSubQuestionRowId(null);
        }}
        subQuestions={editingRow?.sub_questions_data || []}
        onSave={handleSubQuestionSave}
      />
    </Dialog>
  );
};

export default MultiQuestionEditorDialog;
